/**
 * Cockpit feat-cockpit — the closed icon set for the `Icon` catalog component.
 *
 * AIR-GAP CONSTRAINT (CLAUDE.md Rule #12): the in-iframe vanilla renderer cannot
 * load `lucide-react`, a font-icon stylesheet, or any CDN — the sandbox is
 * null-origin with `script-src/style-src 'self'` and no network. So the cockpit
 * ships a CURATED set of ~40 common icon path strings (the SVG `<path d="…">`
 * geometry, lifted from the MIT-licensed lucide icon set — lucide.dev, ISC/MIT)
 * that the vanilla renderer draws via `document.createElementNS('svg')` +
 * `<path>`. The React renderer maps the SAME names to `lucide-react` components
 * so the two stay visually consistent.
 *
 * Each entry is one or more SVG path `d` strings drawn inside a `0 0 24 24`
 * viewBox with `fill="none" stroke="currentColor" stroke-width="2"
 * stroke-linecap="round" stroke-linejoin="round"` (the lucide default). Names
 * are the lucide kebab-case slugs. The set is intentionally finite + frozen so
 * the gates can guarantee a known surface and the bundle stays tiny.
 *
 * Browser-safe: pure data, zero imports — shared by api (lint), web (React +
 * name validation) and ai-core (vanilla SVG draw).
 */

/**
 * name → array of SVG path `d` strings (an icon may be several strokes/shapes).
 * A few icons also need a non-path primitive (circle/line); those are encoded as
 * extra path `d` approximations so the vanilla renderer only ever emits
 * `<path>` (one code path, easy to audit). Curated for industrial cockpits:
 * status, navigation, assets, actions, alerts, data.
 */
export const COCKPIT_ICON_PATHS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  /* ── status / alerts ─────────────────────────────────────────────────── */
  'alert-triangle': ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
  'alert-circle': ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 8v4', 'M12 16h.01'],
  'check-circle': ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4 12 14.01l-3-3'],
  check: ['M20 6 9 17l-5-5'],
  x: ['M18 6 6 18', 'M6 6l12 12'],
  'x-circle': ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M15 9l-6 6', 'M9 9l6 6'],
  info: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 16v-4', 'M12 8h.01'],
  bell: ['M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9', 'M10.3 21a1.94 1.94 0 0 0 3.4 0'],
  shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
  'shield-check': ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 'M9 12l2 2 4-4'],
  zap: ['M13 2 3 14h9l-1 8 10-12h-9l1-8z'],
  flame: ['M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z'],

  /* ── navigation / chevrons ───────────────────────────────────────────── */
  'chevron-right': ['M9 18l6-6-6-6'],
  'chevron-down': ['M6 9l6 6 6-6'],
  'chevron-up': ['M18 15l-6-6-6 6'],
  'chevron-left': ['M15 18l-6-6 6-6'],
  'arrow-up': ['M12 19V5', 'M5 12l7-7 7 7'],
  'arrow-down': ['M12 5v14', 'M19 12l-7 7-7-7'],
  'arrow-right': ['M5 12h14', 'M12 5l7 7-7 7'],
  'external-link': ['M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6', 'M15 3h6v6', 'M10 14 21 3'],
  search: ['M11 2a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M21 21l-4.35-4.35'],
  menu: ['M4 6h16', 'M4 12h16', 'M4 18h16'],
  filter: ['M22 3H2l8 9.46V19l4 2v-8.54L22 3z'],
  settings: ['M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'],

  /* ── assets / industrial ─────────────────────────────────────────────── */
  gauge: ['M12 14l4-4', 'M3.34 19a10 10 0 1 1 17.32 0'],
  activity: ['M22 12h-4l-3 9L9 3l-3 9H2'],
  cpu: ['M4 4h16v16H4z', 'M9 9h6v6H9z', 'M9 1v3', 'M15 1v3', 'M9 20v3', 'M15 20v3', 'M20 9h3', 'M20 14h3', 'M1 9h3', 'M1 14h3'],
  database: ['M12 2c4.97 0 9 1.343 9 3s-4.03 3-9 3-9-1.343-9-3 4.03-3 9-3z', 'M3 5v6c0 1.657 4.03 3 9 3s9-1.343 9-3V5', 'M3 11v6c0 1.657 4.03 3 9 3s9-1.343 9-3v-6'],
  server: ['M3 3h18v6H3z', 'M3 15h18v6H3z', 'M7 6h.01', 'M7 18h.01'],
  wrench: ['M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.83 2.83-2.83-2.83 2.83-2.83z'],
  thermometer: ['M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z'],
  droplet: ['M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z'],
  battery: ['M2 7h16v10H2z', 'M22 11v2'],
  power: ['M18.36 6.64a9 9 0 1 1-12.73 0', 'M12 2v10'],
  map: ['M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z', 'M9 3v15', 'M15 6v15'],
  'map-pin': ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],

  /* ── actions ─────────────────────────────────────────────────────────── */
  plus: ['M12 5v14', 'M5 12h14'],
  minus: ['M5 12h14'],
  edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
  trash: ['M3 6h18', 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6'],
  'refresh-cw': ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
  download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
  upload: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
  play: ['M5 3l14 9-14 9V3z'],
  pause: ['M6 4h4v16H6z', 'M14 4h4v16h-4z'],
  clock: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 6v6l4 2'],
  user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
  'file-text': ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
  'bar-chart': ['M12 20V10', 'M18 20V4', 'M6 20v-4'],
  'trending-up': ['M23 6l-9.5 9.5-5-5L1 18', 'M17 6h6v6'],
  'trending-down': ['M23 18l-9.5-9.5-5 5L1 6', 'M17 18h6v-6'],
  circle: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z'],
});

/** Every curated icon name (sorted, frozen). */
export const COCKPIT_ICON_NAMES: readonly string[] = Object.freeze(
  Object.keys(COCKPIT_ICON_PATHS).sort(),
);

/** The fallback icon when a requested name is not in the curated set. */
export const COCKPIT_ICON_FALLBACK = 'circle' as const;

/** True iff `name` is a curated cockpit icon. */
export function isCockpitIcon(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(COCKPIT_ICON_PATHS, name);
}

/** Resolve an icon name to its path strings, falling back to the default. */
export function getCockpitIconPaths(name: string | undefined): readonly string[] {
  if (name && isCockpitIcon(name)) return COCKPIT_ICON_PATHS[name]!;
  return COCKPIT_ICON_PATHS[COCKPIT_ICON_FALLBACK]!;
}
